/**
 * Responsibility: Implement player profile model behavior for the ui/player-profile surface.
 * Scope: Owns ui/player-profile concerns only; cross-surface orchestration is excluded.
 */
import type { Ability, Attribute, AttributeKey, GameState, InventoryItem, Skill, Trait } from "@anyquest/shared-core"
import { calcStat as calcStatValue } from '../../engine/state'
import { getTaxonomyItemCategory } from '../../engine/world-taxonomy'
import { vitalPercent } from '../../engine/vitals'
import { effectChipText as formatEffectChipText } from '../helpers/effect-chip'
import { SHEET_LIMITS } from '../helpers/sheet-contracts'

export type PlayerProfileTab = 'profile' | 'inventory'
export type PlayerVitalKey = 'vitality' | 'spirit' | 'essence'

export type PlayerProfileVital = {
  key: PlayerVitalKey
  label: string
  desc: string
  value: number
  max: number
  percent: number
  tone: 'vitality' | 'spirit' | 'essence'
}

export type PlayerProfileAttributeCard = {
  key: AttributeKey
  value: number
  desc: string
  meta: string
}

export type PlayerProfileListCard = {
  name: string
  desc: string
  meta: string
}

export type PlayerProfileInventoryItem = {
  name: string
  equipped: boolean
  count: number
}

export type PlayerProfileInventoryGroup = {
  key: string
  label: string
  items: PlayerProfileInventoryItem[]
  total: number
  overflow: number
}

export type PlayerProfileEffectChip = {
  label: string
  text: string
  dormant: boolean
}

function clampList<T>(items: T[], limit: number) {
  const safeLimit = Math.max(0, Number(limit || 0))
  return {
    items: items.slice(0, safeLimit),
    overflow: Math.max(0, items.length - safeLimit),
  }
}

type AggregatedInventoryItem = PlayerProfileInventoryItem & {
  categoryKey?: string
  categoryLabel?: string
}

function formatInventoryCategoryLabel(category: string): string {
  const label = String(category ?? '').replace(/_/g, ' ').trim()
  if (!label) return 'Misc'
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`
}

function aggregateInventory(entries: InventoryItem[], equipped: boolean, gs: GameState | null): AggregatedInventoryItem[] {
  const byKey = new Map<string, AggregatedInventoryItem>()
  const taxonomy = gs?.world?.taxonomy
  for (const entry of entries) {
    const name = String(entry?.name ?? '').trim()
    if (!name || !!entry?.equipped !== equipped) continue
    const key = name.toLowerCase()
    const categoryKey = taxonomy ? (getTaxonomyItemCategory(taxonomy, name) ?? 'misc') : undefined
    const categoryLabel = categoryKey ? formatInventoryCategoryLabel(categoryKey) : undefined
    const existing = byKey.get(key)
    if (existing) {
      existing.count += 1
    } else {
      byKey.set(key, { name, equipped, count: 1, categoryKey, categoryLabel })
    }
  }
  return [...byKey.values()].sort((a, b) => {
    const byCategory = String(a.categoryLabel ?? '').localeCompare(String(b.categoryLabel ?? ''))
    if (byCategory !== 0) return byCategory
    return a.name.localeCompare(b.name)
  })
}

function skillCard(skill: Skill): PlayerProfileListCard {
  return {
    name: skill.name,
    desc: skill.desc || 'Learned proficiency used in relevant checks.',
    meta: `LVL ${skill.level}`,
  }
}

function abilityCard(ability: Ability): PlayerProfileListCard {
  return {
    name: ability.name,
    desc: ability.desc || 'No description.',
    meta: `CD ${ability.cooldown}m`,
  }
}

function activeEffectLabels(gs: GameState | null) {
  const nowMin = gs
    ? (gs.world.clock.day * 24 * 60) + (gs.world.clock.hour * 60) + gs.world.clock.minute
    : 0
  const labels: string[] = []
  for (const mods of Object.values(gs?.player?.modifiers ?? {})) {
    for (const mod of mods ?? []) {
      if (mod.expiresAt != null && mod.expiresAt <= nowMin) continue
      labels.push(mod.label || mod.source || 'Effect')
    }
  }
  return [...new Set(labels)]
}

function dormantEffectLabels(gs: GameState | null) {
  return [...new Set((gs?.player?.dormantEffects ?? []).map(effect => effect.label || effect.source || 'Effect'))]
}

function collectEffectCards(labels: unknown[], dormant: boolean): PlayerProfileEffectChip[] {
  const unique = [...new Set((Array.isArray(labels) ? labels : []).map(label => String(label ?? '').trim()).filter(Boolean))]
  const clamped = clampList(unique, dormant ? SHEET_LIMITS.list.dormantEffects : SHEET_LIMITS.list.activeEffects)
  return clamped.items.map(label => ({
    label,
    text: formatEffectChipText(label, dormant),
    dormant,
  }))
}

export class PlayerProfileModel {
  static empty() {
    return new PlayerProfileModel(null)
  }

  static fromGameState(gs: GameState | null | undefined) {
    return new PlayerProfileModel(gs ?? null)
  }

  gs: GameState | null
  playerEffectsRaw: unknown[]
  playerDormantEffectsRaw: unknown[]

  constructor(gs: GameState | null) {
    this.gs = gs
    this.playerEffectsRaw = activeEffectLabels(gs)
    this.playerDormantEffectsRaw = dormantEffectLabels(gs)
  }

  get isReady() {
    return !!this.gs
  }

  get player() {
    return this.gs?.player ?? null
  }

  get portraitUrl() {
    return this.gs?.portraitUrl || ''
  }

  get portraitInitials() {
    return String(this.player?.name || this.player?.identity || '?').slice(0, 2).toUpperCase()
  }

  get titleName() {
    return this.player?.name || this.player?.identity || 'Unknown Vessel'
  }

  get identity() {
    return this.player?.identity || ''
  }

  get sex() {
    return this.player?.sex || ''
  }

  get bio() {
    return this.player?.bio || ''
  }

  get currentRoomName() {
    return this.gs?.currentRoom?.name || 'Unknown'
  }

  get timeLabel() {
    const clock = this.gs?.world?.clock
    if (!clock) return ''
    return `DAY ${clock.day} · ${String(clock.hour).padStart(2, '0')}:${String(clock.minute).padStart(2, '0')}`
  }

  get vitals(): PlayerProfileVital[] {
    const vitals = this.player?.vitals
    if (!vitals) return []
    return [
      {
        key: 'vitality',
        label: '❤ VITALITY',
        desc: 'Your physical durability. Low vitality makes combat and hazardous actions far riskier.',
        value: Math.max(0, Number(vitals.vitality || 0)),
        max: Math.max(1, Number(vitals.maxVitality || vitals.vitality || 1)),
        percent: vitalPercent(vitals, 'vitality'),
        tone: 'vitality',
      },
      {
        key: 'spirit',
        label: '✦ SPIRIT',
        desc: 'Your mental and emotional reserve. Spirit supports social pressure, focus, and strain resistance.',
        value: Math.max(0, Number(vitals.spirit || 0)),
        max: Math.max(1, Number(vitals.maxSpirit || vitals.spirit || 1)),
        percent: vitalPercent(vitals, 'spirit'),
        tone: 'spirit',
      },
      {
        key: 'essence',
        label: '✿ ESSENCE',
        desc: 'Your core life force. Essence fuels mystical effects and is costly to recover when depleted.',
        value: Math.max(0, Number(vitals.essence || 0)),
        max: Math.max(1, Number(vitals.maxEssence || vitals.essence || 1)),
        percent: vitalPercent(vitals, 'essence'),
        tone: 'essence',
      },
    ]
  }

  get attributes(): PlayerProfileAttributeCard[] {
    if (!this.gs?.player?.attributes) return []
    return (Object.entries(this.gs.player.attributes) as Array<[AttributeKey, Attribute]>)
      .map(([key, attribute]) => ({
        key,
        value: attribute.val,
        desc: `${attribute.fullName || key} — ${attribute.desc}\n\n${attribute.aiGuidance || 'Core stat.'}`,
        meta: `ATTRIBUTE · BASE ${attribute.val}`,
      }))
  }

  get traits(): Trait[] {
    return Array.isArray(this.player?.traits) ? this.player.traits : []
  }

  get skills() {
    const list = (Array.isArray(this.player?.skills) ? this.player.skills : []).map(skillCard)
    return clampList(list, SHEET_LIMITS.list.abilities)
  }

  get abilities() {
    const list = (Array.isArray(this.player?.abilities) ? this.player.abilities : []).map(abilityCard)
    return clampList(list, SHEET_LIMITS.list.abilities)
  }

  get inventoryGroups(): PlayerProfileInventoryGroup[] {
    const inventory = Array.isArray(this.player?.inventory) ? this.player.inventory : []
    const equippedItems = aggregateInventory(inventory, true, this.gs)
    const carriedItems = aggregateInventory(inventory, false, this.gs)
    const taxonomy = this.gs?.world?.taxonomy
    if (!taxonomy) {
      const equipped = clampList(equippedItems, SHEET_LIMITS.list.inventory)
      const carried = clampList(carriedItems, SHEET_LIMITS.list.inventory)
      return [
        { key: 'equipped', label: 'Equipped', items: equipped.items, total: equipped.items.length + equipped.overflow, overflow: equipped.overflow },
        { key: 'carried', label: 'Carried', items: carried.items, total: carried.items.length + carried.overflow, overflow: carried.overflow },
      ]
    }

    const groups = new Map<string, PlayerProfileInventoryGroup>()
    for (const item of [...equippedItems, ...carriedItems]) {
      const ownerKey = item.equipped ? 'equipped' : 'carried'
      const ownerLabel = item.equipped ? 'Equipped' : 'Carried'
      const categoryKey = item.categoryKey ?? 'misc'
      const categoryLabel = item.categoryLabel ?? 'Misc'
      const groupKey = `${ownerKey}:${categoryKey}`
      const existing = groups.get(groupKey)
      if (existing) {
        existing.items.push({ name: item.name, equipped: item.equipped, count: item.count })
        existing.total += 1
      } else {
        groups.set(groupKey, {
          key: groupKey,
          label: `${ownerLabel} · ${categoryLabel}`,
          items: [{ name: item.name, equipped: item.equipped, count: item.count }],
          total: 1,
          overflow: 0,
        })
      }
    }

    const ordered = [...groups.values()].sort((a, b) => a.label.localeCompare(b.label))
    return ordered.map(group => {
      const clamped = clampList(group.items, SHEET_LIMITS.list.inventory)
      return {
        ...group,
        items: clamped.items,
        total: group.total,
        overflow: clamped.overflow,
      }
    })
  }

  get inventoryTotalCount() {
    return (Array.isArray(this.player?.inventory) ? this.player.inventory : []).length
  }

  get inventoryUniqueCount() {
    return this.inventoryGroups.reduce((sum, group) => sum + group.total, 0)
  }

  get activeEffects() {
    const chips = collectEffectCards(this.playerEffectsRaw, false)
    return {
      items: chips,
      overflow: Math.max(0, this.playerEffectsRaw.length - chips.length),
    }
  }

  get dormantEffects() {
    const chips = collectEffectCards(this.playerDormantEffectsRaw, true)
    return {
      items: chips,
      overflow: Math.max(0, this.playerDormantEffectsRaw.length - chips.length),
    }
  }

  get hasStatusEffects() {
    return this.activeEffects.items.length > 0 || this.dormantEffects.items.length > 0
  }
}
